const SCROLL_SPEED_PX_PER_SECOND = 30;

/** A single repetition is a message span plus its trailing separator span. */
const ELEMENTS_PER_COPY = 2;

class CautivaAnnouncementBar extends HTMLElement {
  #abortController;
  #resizeObserver;

  connectedCallback() {
    const storageKey = this.dataset.storageKey;
    if (storageKey && this.#isDismissed(storageKey)) {
      this.hidden = true;
      return;
    }

    this.#startScrolling();

    const dismissButton = this.querySelector('.cautiva-announcement__dismiss');
    if (!dismissButton) return;

    this.#abortController = new AbortController();
    dismissButton.addEventListener('click', () => this.#dismiss(storageKey), {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#resizeObserver?.disconnect();
  }

  #startScrolling() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const viewport = this.querySelector('.cautiva-announcement__viewport');
    const groups = this.querySelectorAll('.cautiva-announcement__group');
    if (!viewport || groups.length !== 2) return;

    // The track only renders in full with this class on, so it goes on before
    // anything is measured — and outside the observer callback, so the observer
    // never witnesses a resize it caused itself.
    this.classList.add('cautiva-announcement--scrolling');

    // ResizeObserver runs after layout and before paint, so the first delivery
    // starts the ticker without a visible static frame. Later deliveries matter
    // because a widened viewport can outgrow the repeats that filled it at load.
    this.#resizeObserver = new ResizeObserver(() => this.#fitTrack(viewport, groups));
    this.#resizeObserver.observe(viewport);

    // A webfont swapping in after the first measurement changes every width,
    // which both skews the pace and can shrink a group back below the viewport —
    // reopening the gap the repeats existed to close.
    document.fonts?.ready.then(() => {
      if (this.isConnected) this.#fitTrack(viewport, groups);
    });
  }

  /**
   * Repeats the message until one group spans the viewport — the second group
   * then covers the gap the first leaves as it exits, so translateX(-50%) loops
   * seamlessly — and paces the animation at a constant px/s so a short message
   * doesn't crawl and a long one doesn't race.
   */
  #fitTrack(viewport, groups) {
    const [leadGroup] = groups;
    // Fractional widths on both sides: clientWidth rounds to an integer, which
    // can hide a sub-pixel shortfall at the exact-fit boundary.
    const viewportWidth = viewport.getBoundingClientRect().width;
    let groupWidth = leadGroup.getBoundingClientRect().width;
    if (!viewportWidth || !groupWidth) return;

    if (groupWidth < viewportWidth) {
      const copyWidth = groupWidth / (leadGroup.childElementCount / ELEMENTS_PER_COPY);
      const copiesToAdd = Math.ceil((viewportWidth - groupWidth) / copyWidth);

      for (const group of groups) {
        // The second copy, not the first — the first carries the accessible
        // name and any link, and must stay unique in the document.
        const template = Array.from(group.children).slice(ELEMENTS_PER_COPY, ELEMENTS_PER_COPY * 2);
        for (let added = 0; added < copiesToAdd; added += 1) {
          group.append(...template.map((element) => element.cloneNode(true)));
        }
      }
      groupWidth = leadGroup.getBoundingClientRect().width;
    }

    this.style.setProperty(
      '--cautiva-announcement-duration',
      `${groupWidth / SCROLL_SPEED_PX_PER_SECOND}s`
    );
  }

  #isDismissed(key) {
    try {
      return window.localStorage.getItem(key) === 'dismissed';
    } catch {
      return false;
    }
  }

  #dismiss(key) {
    if (key) {
      try {
        window.localStorage.setItem(key, 'dismissed');
      } catch {}
    }
    this.hidden = true;
  }
}

if (!customElements.get('cautiva-announcement-bar')) {
  customElements.define('cautiva-announcement-bar', CautivaAnnouncementBar);
}
